import React, { useEffect, useState } from "react";
import { Redirect } from "react-router-dom";

import {
  Alert,
  Button,
  Card,
  CardStackSection,
  Heading,
  Stack,
  Text,
} from "@origin-digital/ods-core";
import { IValidator, required, validEmail } from "@origin-digital/validation";
import { reportException } from "@origin-digital/reporting-client";
import { logout } from "@origin-digital/event-dispatcher";
import { useToggle } from "@origin-digital/toggle";
import { useTrackingContext } from "src/context/TrackingContext";
import { useTextFieldState } from "src/hooks";
import { pageRoutes } from "src/consts";
import { reduceValidators } from "src/utils/helpers";
import { errorTracking, formTracking } from "src/utils/tracking";
import {
  hideNotification,
  showErrorNotification,
} from "src/utils/notifications";
import { Form } from "src/components/Form";
import { TextField } from "src/components/TextField";
import { FieldUpdateHook } from "../../../crm/profileFieldMap";
import { ProfileFieldMapTypes } from "../../../crm/profileFieldMapContextHelpers";
import content from "./content.json";

export interface IProps {
  email: string | null;
  useUpdate: FieldUpdateHook<string>;
  type: ProfileFieldMapTypes;
}

export const UpdateEmailScreen: React.FC<IProps> = ({
  email: currentEmail,
  useUpdate,
  type,
}) => {
  const [updateEmail, { success, loading, error }] = useUpdate();
  const trackingContext = useTrackingContext();
  const emailState = useTextFieldState("");
  const logoutOnEmailChange = useToggle("logoutUserOnEmailChange");
  const [redirecting, setRedirecting] = useState<boolean>(false);

  useEffect(() => {
    if (error) {
      showErrorNotification(content.submitError);
      errorTracking(error, type);
      reportException("UpdateEmailScreen.updateEmail()", error);
    }
  }, [error]);

  useEffect(() => {
    if (success && !error) {
      formTracking("complete", trackingContext, type);
      if (logoutOnEmailChange) {
        logout(encodeURIComponent("/auth/#/update-email/success"));
      } else {
        setRedirecting(true);
      }
    }
  }, [success, error]);

  const handleValidate = (): string => {
    const duplicateEmail: IValidator = ({ error: errorMessage }) => {
      return (value) => {
        if (value === currentEmail) {
          return errorMessage;
        }

        return undefined;
      };
    };

    const { errors } = content.email;

    const validators = [
      required({ error: errors.required }),
      validEmail({ error: errors.invalid }),
      duplicateEmail({ error: errors.duplicate }),
    ];

    const errorMessage = reduceValidators(emailState.value, validators);

    emailState.setError(errorMessage);

    return errorMessage;
  };

  const handleSubmit = () => {
    formTracking("start", trackingContext, type);

    if (handleValidate()) {
      return;
    }

    emailState.setFocus(false);
    hideNotification();

    updateEmail(emailState.value.toLowerCase());
  };

  if (redirecting) {
    return <Redirect push to={pageRoutes.updateEmail.success} />;
  }

  return (
    <CardStackSection data-id="update-email">
      <Card>
        <Form onSubmit={handleSubmit}>
          <Stack space={["large", "xlarge"]}>
            <Heading data-id="heading" variant="h3">
              {content.heading}
            </Heading>
            <Stack space={["medium", "large"]}>
              {currentEmail ? (
                <Text data-id="email-message" wordBreak>
                  {currentEmail}
                </Text>
              ) : null}
              <Alert data-id="info-message">
                <Text>{content.info}</Text>
              </Alert>
              <TextField
                disabled={loading}
                handleValidate={handleValidate}
                id="email-address"
                label={content.email.label}
                textFieldState={emailState}
                type="email"
                onFocus={() => formTracking("start", trackingContext, type)}
              />
            </Stack>
            <Button data-id="submit" fullWidth type="submit" spinner={loading}>
              {content.submit}
            </Button>
          </Stack>
        </Form>
      </Card>
    </CardStackSection>
  );
};
